import React from "react";
import { useSelector } from "react-redux";
import { Award, Trophy } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import CurrencyConverter from "../../Currency/CurrencyConverter";

const RankAdvancementDashboard = ({ data }) => {
  const { t } = useTranslation();
  const isLoading = !data;


  const skeletonStyle = {
    backgroundColor: "#2c3136",
    backgroundImage: isLoading 
      ? "linear-gradient(90deg, #2c3136 0%, #3a3f44 50%, #2c3136 100%)"
      : "none",
    backgroundSize: "200% 100%",
    animation: isLoading ? "skeleton-loading 1.5s infinite" : "none",
  };

  const currency = useSelector((state) => state.user?.selectedCurrency);
  const conversionFactor = useSelector((state) => state.user?.conversionFactor);

  const skeletonBox = {
    height: "16px",
    backgroundColor: "#e0e0e0",
    borderRadius: "4px",
    marginBottom: "8px",
    width: "100%",
    animation: "pulse 1.5s infinite ease-in-out",
  };

  const bigSkeletonBox = {
    ...skeletonBox,
    height: "24px",
    width: "80%",
  };

  const skeletonImage = {
    width: "80px",
    height: "40px",
    backgroundColor: "#ddd",
    borderRadius: "50%",
    animation: "pulse 1.5s infinite ease-in-out",
  };

  const renderSkeletonStat = () => (
    <div className="charts-container">
      {[...Array(3)].map((_, idx) => (
        <div className="stat-item" key={idx}>
          <div style={{ ...skeletonBox, width: "60%" }} />
          <div style={{ ...skeletonBox, width: "40%" }} />
        </div>
      ))}
    </div>
  );

  const renderSkeletonTable = () => (
    <table className="pv-table">
      <thead>
        <tr>
          <th>Level</th>
          <th>Total PV</th>
        </tr>
      </thead>
      <tbody>
        {[...Array(5)].map((_, index) => (
          <tr key={index}>
            <td>
              <div style={{ ...skeletonBox, width: "60px" }} />
            </td>
            <td>
              <div style={{ ...skeletonBox, width: "80px" }} />
            </td>
          </tr>
        ))}
        <tr className="total-row">
          <td>Total</td>
          <td>
            <div style={{ ...skeletonBox, width: "80px" }} />
          </td>
        </tr>
      </tbody>
    </table>
  );

  return (
    <div className="col-md-16">
      <div className="joinings_viewBox">
        <h5>{t("rankAdvancementSummary")}</h5>
        {data?.isMax ? (
          <>
            <div className="rank-header-banner max-rank-banner">
              <div className="rank-title-container">
                <h1 className="rank-title max-rank-title">
                  <span className="achievement-text">
                  Maximum Advancement Rank Bonus Achieved!
                  </span>
                  <div className="celebration-icon">🏆</div>
                </h1>
              </div>

              {/* Enhanced Top Rank Display */}
              <div className="top-rank-container">
                <div className="top-rank-content">
                  <div className="rank-achievement-badge">
                    {/* <div className="rank-shine-effect"></div> */}
                    {data?.nextRank?.image ? (
                      <div className="top-rank-icon">
                        <img
                          src={data?.nextRank?.image}
                          alt="Rank Badge"
                          className="rank-image-glow"
                        />
                      </div>
                    ) : (
                      <div className="top-rank-icon animated-trophy">
                        <Trophy size={64} color="gold" />
                      </div>
                    )}
                    <div className="top-rank-name">
                      <span className="rank-name-text">
                        {data?.nextRank?.name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="rank-subtitle max-rank-subtitle">
                <span className="congratulation-text">Congratulations!</span>{" "}
                You've reached the highest Advancement rank bonus in the system!
                <span className="celebration-emoji">🎉</span>
              </p>

              <div className="achievement-stars">
                <span className="star-left">✨</span>
                <span className="star-center">⭐</span>
                <span className="star-right">✨</span>
              </div>
            </div>
          </>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}>
            <div style={{ flex: "1", minWidth: "320px" }}>
              <div className="rank-header-banner">
                <div className="rank-title-container">
                  <h1 className="rank-title">
                    {isLoading ? (
                      <div style={{ ...bigSkeletonBox }} />
                    ) : (
                      `Next Rank Advancement Bonus : ${data?.nextRank?.name}`
                    )}
                  </h1>
                </div>

                <div className="rank-banner-content">
                  {isLoading ? (
                    <div className="rank-icon-container">
                      <div style={skeletonImage} />
                    </div>
                  ) : data?.nextRank?.image ? (
                    <div className="rank-icon-container">
                      <img src={data?.nextRank?.image} alt="" />
                    </div>
                  ) : (
                    <div className="rank-icon-container">
                      <Award size={22} color="#fff" />
                    </div>
                  )}

                  <div className="rank-status-badge">
                    <span>
                      {isLoading ? (
                        <div
                          style={{
                            ...skeletonBox,
                            width: "80px",
                            marginTop: ".4rem",
                          }}
                        />
                      ) : (
                        `${data?.totalPvRequirement?.percentage}% Complete`
                      )}
                    </span>
                  </div>
                </div>

                <p className="rank-subtitle" style={{ fontSize: "small" }}>
                  {isLoading ? (
                    <div style={{ ...skeletonBox, width: "80%" }} />
                  ) : (
                    "Keep pushing to reach the next Bonus!"
                  )}
                </p>
              </div>
              

              <div className="rank-main-content">
                <div className="rank-card summary-stats-card">
                  <div className="rank-card-header">
                    <h3>Summary</h3>
                  </div>

                  

                  {isLoading ? (
                    renderSkeletonStat()
                  ) : (
                    <div className="charts-container">
                      <div className="stat-item current-pv">
                        <div className="stat-title">Current Total Volume</div>
                        <div className="stat-value">
                          <strong>{`${
                            currency?.symbolLeft ? currency?.symbolLeft : "$"
                          } ${CurrencyConverter(
                            data?.totalPvRequirement?.currentCappedPv ?? 0,
                            conversionFactor
                          )}`}</strong>
                        </div>
                      </div>

                      <div className="stat-item required-pv">
                        <div className="stat-title">
                          Required for {data?.nextRank?.name}
                        </div>
                        <div className="stat-value">
                          <strong>{`${
                            currency?.symbolLeft ? currency?.symbolLeft : "$"
                          } ${CurrencyConverter(
                            data?.totalPvRequirement?.required ?? 0,
                            conversionFactor
                          )}`}</strong>
                        </div>
                      </div>

                      <div className="stat-item remaining-pv">
                        <div className="stat-title">
                          Remaining Volume Needed
                        </div>
                        <div className="stat-value">
                          <strong>{`${
                            currency?.symbolLeft ? currency?.symbolLeft : "$"
                          } ${CurrencyConverter(
                            data?.totalPvRequirement?.remaining ?? 0,
                            conversionFactor
                          )}`}</strong>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

                  <div
                   style={{
                    display: "inline-flex",
                    marginTop: ".4rem",
                    marginBottom: "1rem",
                    alignItems: "center",
                    padding: "0.8rem 0.75rem",
                    width: "100%",
                    justifyContent: "center",
                    backgroundImage: "linear-gradient(10deg, #000, #6f6f6f)",
                    borderRadius: "4px",
                    fontFamily: "system-ui, sans-serif",
                    color: "#adb5bd",
                    fontSize: "0.87rem",
                  }}                  
                    className="stat-item remaining-pv"
                  >
                    {data?.currentRank?.isCurrentRankExist ? (
                      <>
                        <span style={{ fontWeight: 500 }}>Current Bonus :</span>
                        <span
                          style={{
                            fontWeight: 600,
                            marginLeft: "4px",
                          }}
                        >
                          {data?.currentRank?.name}
                        </span>
                        <span
                          style={{
                            fontWeight: 700,
                            color: "#ffffff",
                            marginLeft: "4px",
                          }}
                        >
                          <strong>{` ${CurrencyConverter(
                            data?.currentRank?.currentRankPv ?? 0,
                            conversionFactor
                          )} ${
                            currency?.symbolLeft ? currency?.symbolLeft : "USDC"
                          }`}</strong> 
                        </span>
                      </>
                    ) : (
                       isLoading ?  <div 
                       style={{
                         ...skeletonStyle,
                         width: "60%",
                         height: "16px",
                         borderRadius: "4px",
                         marginRight: "8px",
                       }}
                     /> :  <div style={{ color: 'white'}}>No Rank Advancement Achieved</div>
                    )}
                  </div>

                  
            </div>

            <div
              style={{ paddingBottom: "2rem", minWidth: "200px", flex: "2" }}
            >
              <div className="table-wrapper">
                {isLoading ? (
                  renderSkeletonTable()
                ) : (
                  <table className="pv-table">
                    <thead>
                      <tr>
                        <th>Level</th>
                        <th>
                          Total{" "}
                          {currency?.symbolLeft ? currency?.symbolLeft : "$"}
                        </th>
                        <th>
                          Rank Advancement Capping (
                          {data?.totalPvRequirement?.cappingPercentage}%)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Personal Volume</td>
                        <td>{data?.pvBreakdown.personal.toLocaleString()}</td>
                        <td>{data?.pvBreakdown.personal.toLocaleString()}</td>
                      </tr>
                      <tr>
                        <td>Total Capped Volume</td>
                        <td>
                          {data?.totalPvRequirement.totalGroupVolume.toLocaleString()}
                        </td>
                        <td>
                          {data?.totalPvRequirement.totalGroupCapVolume.toLocaleString()}
                        </td>
                      </tr>
                      <tr className="total-row">
                        <td>Total</td>
                        <td>
                          {data?.totalPvRequirement?.current.toLocaleString()}
                        </td>
                        <td>
                          {data?.totalPvRequirement?.currentCappedPv.toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RankAdvancementDashboard;
